// apps/web/src/hooks/use-auth.ts
import useSWR from 'swr'
import { useCallback } from 'react'
import { api, ApiError } from '../lib/api-client'
import { disconnectSocket } from '../lib/socket-client'
import type { StaffUser } from '@viecxanh/chat-shared'

interface LoginPayload {
  email: string
  password: string
}

interface AuthState {
  staff: StaffUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (payload: LoginPayload) => Promise<void>
  logout: () => Promise<void>
}

export function useAuth(): AuthState {
  const { data, error, isLoading, mutate } = useSWR<StaffUser>(
    '/api/admin/auth/me',
    (url: string) => api.get<StaffUser>(url),
    {
      onErrorRetry: (err, _key, _config, revalidate, { retryCount }) => {
        if (err instanceof ApiError && err.status === 401) return
        if (retryCount >= 3) return
        setTimeout(() => revalidate({ retryCount }), 5_000)
      },
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    },
  )

  const login = useCallback(
    async ({ email, password }: LoginPayload) => {
      await api.post('/api/admin/auth/login', { email, password })
      await mutate()
    },
    [mutate],
  )

  const logout = useCallback(async () => {
    await api.post('/api/admin/auth/logout')
    disconnectSocket()
    await mutate(undefined, { revalidate: false })
  }, [mutate])

  const isAuthenticated =
    !isLoading && !(error instanceof ApiError && error.status === 401) && data !== undefined

  return {
    staff: data ?? null,
    isLoading,
    isAuthenticated,
    login,
    logout,
  }
}
